import { KeyboardEvent, MouseEvent, ReactNode, useRef, useState } from 'react';
import { Link } from 'react-router';
import classNames from 'classnames';
import { VerticalDotsIcon } from '@entur/icons';
import { IconButton } from '@entur/button';
import { space } from '@entur/tokens';
import {
  FloatingList,
  FloatingPortal,
  UseInteractionsReturn,
  autoUpdate,
  flip,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useListItem,
  useListNavigation,
  useRole,
  useTypeahead,
} from '@floating-ui/react';
import { Action } from '../types';
import styles from '../styles.module.scss';

interface RowActionsProps<TData> {
  row: TData;
  actions: Action<TData>[];
}

interface MenuItemProps<TData> {
  action: Action<TData>;
  row: TData;
  disabled: boolean;
  activeIndex: number | null;
  getItemProps: UseInteractionsReturn['getItemProps'];
  onSelect: () => void;
}

const MenuItem = <TData,>({ action, row, disabled, activeIndex, getItemProps, onSelect }: MenuItemProps<TData>) => {
  const { ref, index } = useListItem({ label: disabled ? null : action.text });
  const isHighlighted = activeIndex === index;
  const className = classNames('eds-overflow-menu__item', {
    'eds-overflow-menu__item--disabled': disabled,
    'eds-overflow-menu__item--highlighted': isHighlighted,
  });
  const content: ReactNode = (
    <>
      {action.text}&nbsp;{action.icon}
    </>
  );

  if (action.link) {
    return (
      <Link
        ref={ref}
        role="menuitem"
        className={className}
        aria-disabled={disabled}
        to={typeof action.link.to === 'string' ? action.link.to : action.link.to(row)}
        target={action.link.target}
        {...getItemProps({
          tabIndex: isHighlighted ? 0 : -1,
          onClick: disabled ? (event: MouseEvent) => event.preventDefault() : onSelect,
        })}
      >
        {content}
      </Link>
    );
  }

  return (
    <button
      ref={ref}
      type="button"
      role="menuitem"
      className={className}
      aria-disabled={disabled}
      {...getItemProps({
        tabIndex: isHighlighted ? 0 : -1,
        onClick: disabled ? undefined : onSelect,
      })}
    >
      {content}
    </button>
  );
};

export const RowActions = <TData,>({ row, actions }: RowActionsProps<TData>) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const listRef = useRef<(HTMLElement | null)[]>([]);
  const labelsRef = useRef<(string | null)[]>([]);

  const { refs, floatingStyles, context } = useFloating({
    placement: 'bottom-start',
    open: isOpen,
    onOpenChange: setIsOpen,
    middleware: [offset(space.extraSmall2), flip(), shift({ padding: space.extraSmall })],
    whileElementsMounted: autoUpdate,
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'menu' });
  const listNav = useListNavigation(context, {
    listRef,
    activeIndex,
    onNavigate: setActiveIndex,
  });
  const typeahead = useTypeahead(context, {
    listRef: labelsRef,
    activeIndex,
    onMatch: (index) => (isOpen ? setActiveIndex(index) : undefined),
  });

  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions([
    click,
    dismiss,
    role,
    listNav,
    typeahead,
  ]);

  const closeMenuAndReturnFocus = () => {
    setIsOpen(false);
    (refs.reference.current as HTMLElement | null)?.focus?.();
  };

  // useDismiss's built-in escape handling closes the menu without restoring focus, which drops
  // focus entirely once the portaled menu item holding it unmounts. Handle Escape explicitly
  // (on both the trigger and the menu, matching where focus can be) so it always returns to the
  // trigger, like closeMenuAndReturnFocus does for item selection.
  const handleEscapeKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      closeMenuAndReturnFocus();
    }
  };

  return (
    <td className={styles.actions}>
      <IconButton
        size="small"
        aria-label="åpne valgmeny"
        className={styles.actionBtn}
        ref={refs.setReference}
        {...getReferenceProps({ onKeyDown: handleEscapeKeyDown })}
      >
        <VerticalDotsIcon />
      </IconButton>
      {isOpen && (
        <FloatingPortal>
          <div
            // eslint-disable-next-line react-hooks/refs -- refs.setFloating is floating-ui's stable callback ref setter, not a `.current` read
            ref={refs.setFloating}
            style={floatingStyles}
            className="eds-overflow-menu__menu-list"
            {...getFloatingProps({ onKeyDown: handleEscapeKeyDown })}
          >
            <FloatingList elementsRef={listRef} labelsRef={labelsRef}>
              {actions.map((action) => (
                <MenuItem
                  key={action.text}
                  action={action}
                  row={row}
                  disabled={action.disabled ? action.disabled(row) : false}
                  activeIndex={activeIndex}
                  getItemProps={getItemProps}
                  onSelect={() => {
                    action.onClick?.(null, row);
                    closeMenuAndReturnFocus();
                  }}
                />
              ))}
            </FloatingList>
          </div>
        </FloatingPortal>
      )}
    </td>
  );
};
